/**
 * Platform Toast Component
 *
 * Renders toast notifications with auto-dismiss.
 */
import type { ReactNode } from 'react';
import { type Toast as ToastType, usePlatformToast } from '@/hooks/platform/use-platform-toast';

export function ToastContainer() {
  const { toasts, removeToast } = usePlatformToast();

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container" role="region" aria-label="Notifications">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id)} />
      ))}
      <style>{containerStyles}</style>
    </div>
  );
}

interface ToastItemProps {
  toast: ToastType;
  onDismiss: () => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const icons: Record<ToastType['type'], ReactNode> = {
    success: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
          d="M22 11.08V12a10 10 0 11-5.93-9.14"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path d="M22 4L12 14.01l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    error: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    info: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
    warning: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path
          d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  };

  return (
    <div className={`toast toast--${toast.type}`} role="alert" aria-live="polite">
      <span className="toast__icon">{icons[toast.type]}</span>
      <span className="toast__message">{toast.message}</span>
      <button className="toast__dismiss" onClick={onDismiss} aria-label="Dismiss notification">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <style>{toastStyles}</style>
    </div>
  );
}

const containerStyles = `
  .toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    z-index: 10000;
    max-width: 400px;
    pointer-events: none;
  }

  .toast-container > * {
    pointer-events: auto;
  }

  @media (max-width: 480px) {
    .toast-container {
      top: auto;
      bottom: 20px;
      left: 20px;
      right: 20px;
      max-width: none;
    }
  }
`;

const toastStyles = `
  .toast {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 10px;
    background: rgba(15, 23, 42, 0.95);
    border: 1px solid rgba(100, 116, 139, 0.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(8px);
    animation: toast-slide-in 0.3s ease;
  }

  @keyframes toast-slide-in {
    from {
      opacity: 0;
      transform: translateX(100%);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .toast__icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }

  .toast--success {
    border-color: rgba(16, 185, 129, 0.3);
  }

  .toast--success .toast__icon {
    color: #10B981;
  }

  .toast--error {
    border-color: rgba(239, 68, 68, 0.3);
  }

  .toast--error .toast__icon {
    color: #EF4444;
  }

  .toast--info {
    border-color: rgba(0, 229, 255, 0.3);
  }

  .toast--info .toast__icon {
    color: #00E5FF;
  }

  .toast--warning {
    border-color: rgba(245, 158, 11, 0.3);
  }

  .toast--warning .toast__icon {
    color: #F59E0B;
  }

  .toast__message {
    flex: 1;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: #F8FAFC;
    line-height: 1.4;
    padding-top: 2px;
  }

  .toast__dismiss {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: transparent;
    border: none;
    border-radius: 4px;
    color: #64748B;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .toast__dismiss:hover {
    background: rgba(100, 116, 139, 0.2);
    color: #94A3B8;
  }

  @media (max-width: 480px) {
    @keyframes toast-slide-in {
      from {
        opacity: 0;
        transform: translateY(100%);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
`;

export default ToastContainer;
